// Which addresses open the Cinescape app, and how the website sends someone
// there. docs/architecture/app-links-tracking-journeys.md §1 and §6.
//
// Plain JavaScript with no imports beyond the config, so the same rules serve
// the site (the banner, the "Open in the app" pages, /l/{code}), the tests,
// and scripts/check-app-links.mjs, which runs under plain `node`.

import { APP_LINKS } from "./applinks.config.mjs";

export { APP_LINKS };

// §1.2, "must never open the app": the website's own payment and account
// flows return to these. Checked before anything is claimed, and written into
// the Apple file first, marked exclude, for the same reason. The last three
// lines are not in the contract's list; they are further payment and account
// returns this site has, and excluding a page nothing claims changes nothing
// except making that explicit.
export const NEVER_CLAIMED = Object.freeze([
  "/bookingconfirm*",
  "/booking-ticket/*",
  "/payment/*",
  "/seatlayout*",
  "/food/*",
  "/myaccount*",
  "/app/tnc*",
  "/app/privacy*",
  "/app/contactus*",
  "/app/ar/*",
  "/ticket-success.php*",
  "/walletconfirm*",
  "/newpayment*",
  "/clubrecharge-ticket/*",
]);

// §1.2, the claimed paths, first match wins. "*" matches anything, slashes
// included; "?" matches one character. Case is ignored, and a trailing slash
// is covered by listing it (or by the "*"). Only the bare "/" is Home: it must
// never become a prefix that claims everything.
export const CLAIMED = Object.freeze([
  ["/", "home"],
  ["/movies", "movies"],
  ["/movies/", "movies"],
  ["/movie-details/*", "film"],
  ["/moviesessions/*", "film"],
  ["/promotion", "offers"],
  ["/promotion/", "offers"],
  ["/promotion/*", "offer"],
  ["/app/food", "food"],
  ["/app/food/", "food"],
  ["/app/food-order/*", "food_order"],
  ["/app/booking/*", "booking"],
  ["/l/*", "short_link"],
].map((entry) => Object.freeze(entry)));

// Destinations the website answers with a page of its own rather than the
// banner: the "Open in the app" page, or the short-link redirect.
export const OWN_PAGE_DESTINATIONS = new Set(["food", "food_order", "booking", "short_link"]);

// The pages the apps themselves show in a web view (Android Constant.kt and
// MorePageFragment.kt, iOS T&CWebViewController.swift and
// ContactUsTableViewCell.swift). Never offer "open the app" inside the app.
const APP_WEBVIEW_PAGE = /^\/app\/(ar\/)?(tnc|privacy|contactus)\/?$/i;

// The pages that hide the site's header and footer. /app itself or /app/…,
// not any address that merely contains "/app" (a film called "Appaloosa").
const APP_ONLY_PAGE = /^\/app(\/|$)/i;

const SHORT_CODE = /^[A-Za-z0-9_-]{3,32}$/;

const IOS_IN_APP_BROWSER =
  /Instagram|FBAN|FBAV|FB_IAB|FBIOS|musical_ly|BytedanceWebview|TikTok|Snapchat|LinkedInApp|Line\/|MicroMessenger|Pinterest|Twitter/i;
const IOS_OTHER_BROWSER =
  /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|YaBrowser|DuckDuckGo|Ddg\/|UCBrowser|Mercury|Brave/i;

const BANNER_SOURCE = "utm_source=web_banner";
const HAS_SOURCE = /[?&]utm_source=/i;

function globToRegExp(pattern, caseSensitive) {
  const body = pattern
    .split("")
    .map((c) => (c === "*" ? ".*" : c === "?" ? "." : c.replace(/[.+^${}()|[\]\\/]/g, "\\$&")))
    .join("");
  return new RegExp("^" + body + "$", caseSensitive ? "" : "i");
}

export function matchesPattern(path, pattern, caseSensitive = false) {
  return globToRegExp(pattern, caseSensitive).test(path);
}

export function isNeverClaimed(pathname) {
  return NEVER_CLAIMED.some((pattern) => matchesPattern(pathname || "", pattern));
}

// The destination name from §1.2, or null when the path stays on the website.
export function destinationFor(pathname) {
  const path = pathname || "/";
  if (isNeverClaimed(path)) return null;
  const hit = CLAIMED.find(([pattern]) => matchesPattern(path, pattern));
  return hit ? hit[1] : null;
}

export function isAppWebviewPage(pathname) {
  return APP_WEBVIEW_PAGE.test(pathname || "");
}

export function isAppOnlyPage(pathname) {
  return APP_ONLY_PAGE.test(pathname || "");
}

export function isShortCode(code) {
  return typeof code === "string" && SHORT_CODE.test(code);
}

export function isIpAddress(hostname) {
  const host = String(hostname || "");
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(":");
}

// True when the page is on a host the apps claim, over https: only then can a
// link hand the app an https address. An IP address never qualifies.
export function isLinkCapableHost(page, config = APP_LINKS) {
  const hostname = String(page.hostname || "").toLowerCase();
  return (
    page.protocol === "https:" &&
    !isIpAddress(hostname) &&
    config.claimedHosts.includes(hostname)
  );
}

// §1.3. The query with utm_source=web_banner added, unless the address already
// says where it came from: a campaign link's own source is worth more than
// knowing the banner carried it. The rest of the query is kept byte for byte.
export function withBannerSource(search) {
  const query = search || "";
  if (HAS_SOURCE.test(query)) return query;
  return query && query !== "?" ? query + "&" + BANNER_SOURCE : "?" + BANNER_SOURCE;
}

// The browser, from window.navigator or anything with the same fields, or
// just its user-agent string.
function browserFacts(browser) {
  if (browser && typeof browser === "object") {
    return {
      ua: String(browser.userAgent || ""),
      platform: String(browser.platform || ""),
      touchPoints: Number(browser.maxTouchPoints) || 0,
    };
  }
  return { ua: String(browser || ""), platform: "", touchPoints: 0 };
}

// Which kind of browser this is, for choosing how to open the app. Pass
// window.navigator: an iPad asks for the desktop site by default and then
// says "Macintosh" like a Mac, and only its touch screen gives it away.
//   android     any Android browser; an intent:// link works in Chrome and in
//               most in-app browsers there
//   ios_safari  Safari itself: Apple's Smart App Banner already offers Open
//   ios_in_app  Instagram, Facebook, TikTok, Snapchat and other apps' own
//               browsers, which ignore universal links: cinescape:// only.
//               Any iPhone web view that does not call itself Safari counts.
//   ios_other   Chrome, Firefox, Edge and the like on iPhone
//   desktop     everything else
export function browserKind(browser) {
  const { ua, platform, touchPoints } = browserFacts(browser);
  if (/Android/i.test(ua)) return "android";
  const mac = /Macintosh/i.test(ua) || platform === "MacIntel";
  if (/iPhone|iPad|iPod/i.test(ua) || (mac && touchPoints > 1)) {
    if (IOS_IN_APP_BROWSER.test(ua) || !/Safari\//.test(ua)) return "ios_in_app";
    if (IOS_OTHER_BROWSER.test(ua)) return "ios_other";
    return "ios_safari";
  }
  return "desktop";
}

// The address that opens this page in the app, or null on a computer.
// `page` is window.location or anything with the same fields; `browser` is
// window.navigator (or a user-agent string).
//   { href, kind, hint }  hint: show "tap ••• and choose Open in browser".
//
// Android always gets the cinescape:// scheme, on every host. The intent
// names the app's package, so an https address would gain nothing, and would
// lose a visitor who typed or was sent a mixed-case address: the app's https
// filter matches its paths case by case (/movie-details/, never
// /Movie-Details/), so Chrome would fall back to the Play Store with the app
// installed. The scheme's filter has no path rule and the app reads either
// case itself. On the test server there is no Play Store fallback: it would
// install the live app (testServer in applinks.config.mjs).
export function appOpenLink(page, browser, config = APP_LINKS) {
  const kind = browserKind(browser);
  if (kind === "desktop") return null;

  const pathAndQuery = (page.pathname || "/") + withBannerSource(page.search);
  const afterScheme = pathAndQuery.replace(/^\//, "");
  const schemeLink = config.scheme + "://" + afterScheme;
  const linkCapable = isLinkCapableHost(page, config);

  if (kind === "android") {
    return {
      kind,
      hint: false,
      href:
        "intent://" + afterScheme + "#Intent;scheme=" + config.scheme +
        ";package=" + config.androidPackage +
        (config.testServer === true
          ? ""
          : ";S.browser_fallback_url=" + encodeURIComponent(config.playStoreUrl)) +
        ";end",
    };
  }

  if (kind === "ios_in_app") return { kind, hint: true, href: schemeLink };

  if (linkCapable && config.openHostLive) {
    return { kind, hint: false, href: "https://" + config.openHost + pathAndQuery };
  }
  return { kind, hint: false, href: schemeLink };
}

// What the "Open in the Cinescape app" banner links to on this page, or null
// when it should not show. Where it shows is decided here; whether the
// visitor dismissed it, or is inside the app's own web view, is the
// component's business. Never in iPhone Safari while Apple's own banner is
// on the page, on any host: two banners offering the same thing is one too
// many.
export function bannerLink(page, browser, config = APP_LINKS) {
  if (config.bannerEnabled !== true) return null;
  const destination = destinationFor(page.pathname);
  if (!destination || OWN_PAGE_DESTINATIONS.has(destination)) return null;
  const link = appOpenLink(page, browser, config);
  if (!link) return null;
  if (link.kind === "ios_safari" && smartAppBannerContent(page, config) !== null) return null;
  return link;
}

// The content of <meta name="apple-itunes-app">, or null when the page must
// not offer the app, the website offers it nowhere yet (bannerEnabled), or
// this is the test server, where Apple's banner would offer the live app
// (testServer). index.html writes the first one with a copy of this logic,
// because Safari reads the tag while the page loads, before the site's code
// runs; the tests hold the two together.
export function smartAppBannerContent(page, config = APP_LINKS) {
  if (config.bannerEnabled !== true || config.testServer === true) return null;
  const pathname = page.pathname || "/";
  if (isNeverClaimed(pathname)) return null;
  const query = withBannerSource(page.search);
  const argument = isLinkCapableHost(page, config)
    ? page.protocol + "//" + page.host + pathname + query
    : config.scheme + "://" + pathname.replace(/^\//, "") + query;
  return "app-id=" + config.iosAppStoreId + ", app-argument=" + argument.replace(/,/g, "%2C");
}

// §1.4. The address a short link may send the visitor to, or null. The app's
// own address (a test-server link from Lens), or https
// on a host the apps claim, or this site's own origin (the test server serves
// plain http from an IP address). Never another short link, so a bad target
// cannot loop: the path is decoded first, the way the site's router decodes
// it, so /%6C/abc is caught as /l/abc.
export function safeShortLinkTarget(target, page, config = APP_LINKS) {
  if (typeof target !== "string" || target.trim() === "") return null;
  let url;
  try {
    url = new URL(target.trim());
  } catch (e) {
    return null;
  }
  if (isAppAddress(url, config)) {
    if (url.username || url.password || url.port) return null;
    return /^\/+l(\/|$)/i.test(appPath(url)) ? null : url.href;
  }
  const sameOrigin = page && page.origin && url.origin === page.origin;
  const claimed =
    url.protocol === "https:" &&
    !isIpAddress(url.hostname) &&
    config.claimedHosts.includes(url.hostname.toLowerCase());
  if (!sameOrigin && !claimed) return null;
  if (url.username || url.password) return null;
  let path;
  try {
    path = decodeURI(url.pathname);
  } catch (e) {
    return null;
  }
  if (/^\/+l(\/|$)/i.test(path)) return null;
  return url.href;
}

const TRACKING_KEYS = Object.freeze(["utm_source", "utm_medium", "utm_campaign"]);

function trackingValue(params, key) {
  for (const [name, value] of params) {
    if (name.toLowerCase() === key && value.trim() !== "") return value.trim();
  }
  return null;
}

// Lens stores a test-server short link's target as the app's own address,
// cinescape://promotion/77, which reads as host "promotion" and path "/77".
function isAppAddress(url, config) {
  return url.protocol === config.scheme + ":";
}

function appPath(url) {
  let path;
  try {
    path = decodeURI(url.pathname);
  } catch (e) {
    path = url.pathname;
  }
  return "/" + url.hostname + (path === "/" ? "" : path);
}

// §1.4 on a page that cannot hand the app an https address (the test server:
// plain http on an IP address). Sending the visitor to the target there would
// take them off this server to the live website, so the short link's page
// offers the app instead, unless the target is on this very site.
export function shortLinkLeavesSite(target, page, config = APP_LINKS) {
  try {
    const url = new URL(target);
    if (isAppAddress(url, config)) return true;
    if (isLinkCapableHost(page, config)) return false;
    return url.origin !== page.origin;
  } catch (e) {
    return true;
  }
}

// The same address on this site: where "Continue to the website" goes when a
// short link is held on the test server.
export function onThisSite(target, page, config = APP_LINKS) {
  try {
    const url = new URL(target);
    if (isAppAddress(url, config)) return page.origin + appPath(url) + url.search + url.hash;
    return page.origin + url.pathname + url.search + url.hash;
  } catch (e) {
    return "/";
  }
}

// §1.3 and §1.4. The target a short link resolved to, with the short link's
// own utm_source, utm_medium and utm_campaign added where the target has none,
// so /l/summer?utm_source=sms is still counted as the SMS. What the target
// says wins. Both apps do the same (Android DeepLinkParser.followShortLink,
// iOS DeepLinkRouter). The target's own query is kept byte for byte.
export function withShortLinkTracking(target, shortLinkSearch) {
  let url;
  try {
    url = new URL(target);
  } catch (e) {
    return target;
  }
  const outer = new URLSearchParams(shortLinkSearch || "");
  let query = url.search && url.search !== "?" ? url.search : "";
  for (const key of TRACKING_KEYS) {
    const value = trackingValue(outer, key);
    if (value === null || trackingValue(url.searchParams, key) !== null) continue;
    query = (query ? query + "&" : "?") + key + "=" + encodeURIComponent(value);
  }
  url.search = query;
  return url.href;
}

// ---------------------------------------------------------------------------
// The two verification files. Used by the tests and by the live check.
// ---------------------------------------------------------------------------

export function expectedAppId(config = APP_LINKS) {
  return config.iosTeamId + "." + config.iosBundleId;
}

function detailsFor(aasa, appId) {
  const details = (aasa && aasa.applinks && aasa.applinks.details) || [];
  return details.filter(
    (d) => d && ((Array.isArray(d.appIDs) && d.appIDs.includes(appId)) || d.appID === appId)
  );
}

// Does this Apple file claim `path` for `appId`? `style` "components" is how
// iOS 13 and later read it; "paths" is how iOS 12 and earlier read it.
export function aasaClaims(aasa, appId, path, style = "components") {
  for (const detail of detailsFor(aasa, appId)) {
    if (style === "components" && Array.isArray(detail.components)) {
      for (const component of detail.components) {
        if (typeof component["/"] !== "string") continue;
        if (matchesPattern(path, component["/"], component.caseSensitive !== false)) {
          return component.exclude !== true;
        }
      }
    }
    if (style === "paths" && Array.isArray(detail.paths)) {
      for (const entry of detail.paths) {
        const excluded = entry.startsWith("NOT ");
        if (matchesPattern(path, excluded ? entry.slice(4) : entry, true)) return !excluded;
      }
    }
  }
  return false;
}

// Addresses that exercise every row of §1.2 and every never-claim entry: each
// pattern with its "*" filled in, plus near misses that must stay on the site.
export const SAMPLE_PATHS = Object.freeze(
  Array.from(
    new Set([
      ...[...NEVER_CLAIMED, ...CLAIMED.map(([pattern]) => pattern)].map((p) => p.replace(/\*/g, "x1")),
      "/movie-details/dune/1234", "/moviesessions/dune/1234", "/promotion/77",
      "/app/food-order/AB12", "/app/booking/991", "/l/Ab_3",
      "/bookingconfirm", "/seatlayout", "/myaccount", "/myaccount/bookings",
      "/app/tnc", "/app/ar/tnc", "/ticket-success.php", "/walletconfirm", "/newpayment",
      "/experiences", "/cinemasessions/5", "/app", "/app/other", "/promotions",
      "/moviesx", "/l", "/index.html", "/movie-details", "/app/food-order",
    ])
  )
);

// Every way the Apple file disagrees with §1.2, as readable lines. Empty
// means it names the app and claims exactly what this module claims.
export function aasaProblems(aasa, config = APP_LINKS) {
  const problems = [];
  const appId = expectedAppId(config);
  if (!aasa || typeof aasa !== "object" || !aasa.applinks) {
    return ['has no "applinks" section'];
  }
  const details = detailsFor(aasa, appId);
  if (details.length === 0) {
    return ["does not name the app " + appId];
  }
  if (!details.some((d) => Array.isArray(d.appIDs) && d.appIDs.includes(appId))) {
    problems.push('has no "appIDs" entry for ' + appId + " (iOS 13 and later read that)");
  }
  if (!details.some((d) => Array.isArray(d.components) && d.components.length)) {
    problems.push('has no "components" (iOS 13 and later read those)');
  }
  for (const path of SAMPLE_PATHS) {
    const expected = destinationFor(path) !== null;
    const variants = [path, path.toUpperCase()];
    for (const variant of variants) {
      if (aasaClaims(aasa, appId, variant, "components") !== expected) {
        problems.push(variant + (expected ? " should open the app but is not claimed" : " must stay on the website but is claimed"));
      }
    }
    if (aasaClaims(aasa, appId, path, "paths") !== expected) {
      problems.push(path + (expected ? " is not claimed" : " is claimed") + ' in the older "paths" list (iOS 12 and earlier)');
    }
  }
  return problems;
}

const FINGERPRINT = /^([0-9A-F]{2}:){31}[0-9A-F]{2}$/;

// Every way the Android file falls short, as readable lines. A placeholder
// fingerprint is a problem: Android will not verify the app while it is there.
export function assetLinksProblems(statements, config = APP_LINKS) {
  if (!Array.isArray(statements)) return ["is not a JSON list of statements"];
  const ours = statements.filter(
    (s) =>
      s && s.target && s.target.namespace === "android_app" &&
      s.target.package_name === config.androidPackage &&
      Array.isArray(s.relation) &&
      s.relation.includes("delegate_permission/common.handle_all_urls")
  );
  if (ours.length === 0) {
    return ["has no handle_all_urls statement for " + config.androidPackage];
  }
  const problems = [];
  const prints = ours.flatMap((s) => s.target.sha256_cert_fingerprints || []);
  if (prints.length === 0) problems.push("names no signing-key fingerprint");
  for (const print of prints) {
    if (!FINGERPRINT.test(String(print))) {
      problems.push(
        "fingerprint " + JSON.stringify(print) +
        " is not a real SHA-256 fingerprint (32 pairs like AB:CD:…). " +
        "Replace it with the value from Play Console → Setup → App integrity."
      );
    }
  }
  return problems;
}
